import { useState } from 'react';
import CopyButton from './CopyButton.jsx';
import QRModal from './QRModal.jsx';
import { tagColor } from '../utils/tagColor.js';

const COLOR = {
  blue:    { border: 'border-slate-200', bg: 'bg-white', tag: 'bg-indigo-50 text-indigo-700',     accent: 'bg-indigo-600',  btnOpen: 'bg-indigo-600 hover:bg-indigo-700'   },
  teal:    { border: 'border-slate-200', bg: 'bg-white', tag: 'bg-teal-50 text-teal-700',         accent: 'bg-teal-600',    btnOpen: 'bg-teal-600 hover:bg-teal-700'       },
  green:   { border: 'border-slate-200', bg: 'bg-white', tag: 'bg-emerald-50 text-emerald-700',   accent: 'bg-emerald-600', btnOpen: 'bg-emerald-600 hover:bg-emerald-700'  },
  yellow:  { border: 'border-slate-200', bg: 'bg-white', tag: 'bg-amber-50 text-amber-700',       accent: 'bg-amber-500',   btnOpen: 'bg-amber-500 hover:bg-amber-600'      },
  red:     { border: 'border-slate-200', bg: 'bg-white', tag: 'bg-red-50 text-red-700',           accent: 'bg-red-600',     btnOpen: 'bg-red-600 hover:bg-red-700'          },
  pink:    { border: 'border-slate-200', bg: 'bg-white', tag: 'bg-pink-50 text-pink-700',         accent: 'bg-pink-600',    btnOpen: 'bg-pink-600 hover:bg-pink-700'        },
  purple:  { border: 'border-slate-200', bg: 'bg-white', tag: 'bg-violet-50 text-violet-700',     accent: 'bg-violet-600',  btnOpen: 'bg-violet-600 hover:bg-violet-700'    },
  orange:  { border: 'border-slate-200', bg: 'bg-white', tag: 'bg-orange-50 text-orange-700',     accent: 'bg-orange-500',  btnOpen: 'bg-orange-500 hover:bg-orange-600'    },
  cyan:    { border: 'border-slate-200', bg: 'bg-white', tag: 'bg-sky-50 text-sky-700',           accent: 'bg-sky-500',     btnOpen: 'bg-sky-500 hover:bg-sky-600'          },
  lime:    { border: 'border-slate-200', bg: 'bg-white', tag: 'bg-lime-50 text-lime-700',         accent: 'bg-lime-600',    btnOpen: 'bg-lime-600 hover:bg-lime-700'        },
  rose:    { border: 'border-slate-200', bg: 'bg-white', tag: 'bg-rose-50 text-rose-700',         accent: 'bg-rose-600',    btnOpen: 'bg-rose-600 hover:bg-rose-700'        },
  fuchsia: { border: 'border-slate-200', bg: 'bg-white', tag: 'bg-fuchsia-50 text-fuchsia-700',   accent: 'bg-fuchsia-600', btnOpen: 'bg-fuchsia-600 hover:bg-fuchsia-700'  },
};

export default function GemCard({ gem }) {
  const [qrOpen, setQrOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const c = COLOR[tagColor(gem.tag) || gem.color] || COLOR.blue;

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(gem.gem_url); }
    catch {
      const el = document.createElement('textarea');
      el.value = gem.gem_url;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  return (
    <>
      <div className={`rounded-2xl border ${c.border} ${c.bg} shadow-card hover:shadow-card-hover transition-shadow overflow-hidden flex flex-col`}>
        {/* header */}
        <div className={`${c.accent} px-4 py-3.5`}>
          <h3 className="text-white font-bold text-base leading-snug">{gem.name}</h3>
          {gem.tag && (
            <span className="inline-block mt-1.5 text-xs px-2 py-0.5 rounded-full bg-white/20 text-white/90">
              {gem.tag}
            </span>
          )}
        </div>

        {/* body */}
        <div className="p-4 flex-1 flex flex-col gap-3">
          {gem.desc && <p className="text-sm text-slate-600 leading-relaxed">{gem.desc}</p>}

          {gem.system_prompt && (
            <div className="flex flex-col gap-1.5">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">システムプロンプト</p>
              <pre className="text-xs text-slate-700 bg-slate-50 border border-slate-100 rounded-xl p-3 whitespace-pre-wrap line-clamp-3 font-sans leading-relaxed">
                {gem.system_prompt}
              </pre>
              <CopyButton text={gem.system_prompt} label="システムプロンプトをコピー" className="mt-0.5" />
            </div>
          )}

          {gem.example_prompt && (
            <div className="flex flex-col gap-1.5">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">プロンプト例</p>
              <pre className="text-xs text-slate-700 bg-slate-50 border border-slate-100 rounded-xl p-3 whitespace-pre-wrap line-clamp-3 font-sans leading-relaxed">
                {gem.example_prompt}
              </pre>
              <CopyButton text={gem.example_prompt} label="例をコピー" className="mt-0.5" />
            </div>
          )}

          {gem.gem_url && (
            <div className="flex gap-2 mt-auto pt-2 border-t border-slate-100">
              <a
                href={gem.gem_url}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex-1 text-center text-sm font-medium py-2 px-3 rounded-xl text-white ${c.btnOpen} transition-colors cursor-pointer`}
              >
                開く
              </a>
              <button
                onClick={copyLink}
                title="リンクをコピー"
                className={`px-3 py-2 rounded-xl border transition-colors cursor-pointer ${
                  linkCopied
                    ? 'bg-green-50 border-green-200 text-green-600'
                    : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300 hover:text-slate-700'
                }`}
              >
                {linkCopied ? (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                )}
              </button>
              <button
                onClick={() => setQrOpen(true)}
                title="QRコードを表示"
                className="px-3 py-2 rounded-xl border border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:text-slate-700 transition-colors cursor-pointer"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>

      {qrOpen && (
        <QRModal url={gem.gem_url} name={gem.name} onClose={() => setQrOpen(false)} />
      )}
    </>
  );
}
